import { Clipboard } from '@angular/cdk/clipboard';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { DeleteJobGQL } from 'graphql.generated';
import { map, tap, catchError, of, exhaustMap, mergeMap } from 'rxjs';
import { HistoryService } from 'src/app/history/history.service';

import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { JobToolActions } from '../job-tool.actions';
import { selectJob, selectJobId } from '../job-tool.reducer';

import { JobHeaderActions } from './job-header.actions';

export const openJobInSidePanel = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  detailsHelper = inject(DetailsHelperService),
) => {
  return actions$.pipe(
    ofType(JobHeaderActions.jobCodeClicked,),
    concatLatestFrom(() => store.select(selectJobId)),
    tap(([ _action, jobId ]) => detailsHelper.open('job', { id: jobId })),
  );
}, { functional: true, dispatch: false });

export const copyJobDataToClipboard = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  localNotify = inject(FreyaNotificationsService),
  clipboard = inject(Clipboard),
) => {
  return actions$.pipe(
    ofType(JobHeaderActions.jobCodeClicked,),
    concatLatestFrom(() => store.select(selectJob)),
    tap(([ _action, job ]) => {

      if (!navigator.clipboard || !navigator.clipboard.write) {
        clipboard.copy(job.code);
        return;
      }

      const htmlText = `<a href="/jobs/${job.id}/overview?zone=${job.zone.id}">${job.code}</a>`;

      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([ job.code ], { type: 'text/plain' }),
        'text/html': new Blob([ htmlText ], { type: 'text/html' }),
      });

      navigator.clipboard.write([ clipboardItem ]).then(
        () => localNotify.info('Job code and link copied to clipboard', job.code),
        (err) => console.error('Failed to copy job code:', err)
      );
    }),
  );
}, { functional: true, dispatch: false });

export const viewJobHistory = createEffect((
  actions$ = inject(Actions),
  historyService = inject(HistoryService),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobHeaderActions.viewJobHistoryMenuItemClicked),
      concatLatestFrom(() => store.select(selectJobId)),
      tap(([ _action, jobId ]) => historyService.openHistory('Job', [ jobId ])),
    );
}, { functional: true, dispatch: false });

export const deleteJob = createEffect((
  actions$ = inject(Actions),
  deleteJobGQL = inject(DeleteJobGQL),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobHeaderActions.deleteJobMenuItemClicked),
      exhaustMap((action) => of(action).pipe(
        concatLatestFrom(() => store.select(selectJobId)),
        mergeMap(([ _action, jobId ]) => deleteJobGQL.mutate({ jobIds: [ jobId ] }).pipe(
          tap((res) => {

            const success = res.data?.deleteJobs;

            if (!success) {
              throw new Error('Failed to delete job');
            }
          }),
          map(() => JobToolActions.deleteJobSuccess({ jobId })),
        )),
        catchError((error) => of(JobToolActions.deleteJobError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true },
);
